import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import {
  Contact,
  CreateContactData,
  ApiError,
  PaginatedResponse,
} from '../../types/api';

interface UseContactsState {
  contacts: Contact[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

interface UseContactsReturn extends UseContactsState {
  fetchContacts: (params?: { page?: number; limit?: number }) => Promise<void>;
  createContact: (data: CreateContactData) => Promise<Contact | null>;
  deleteContact: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export const useContacts = (
  initialParams?: { page?: number; limit?: number },
  autoFetch: boolean = false
): UseContactsReturn => {
  const [state, setState] = useState<UseContactsState>({
    contacts: [],
    loading: false,
    error: null,
    pagination: null,
  });

  const [queryParams, setQueryParams] = useState<{ page?: number; limit?: number }>(
    initialParams || { page: 1, limit: 10 }
  );

  const fetchContacts = useCallback(async (params?: { page?: number; limit?: number }) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response: PaginatedResponse<Contact> = await api.contacts.getAll(
        params || queryParams
      );

      setState({
        contacts: response.data,
        loading: false,
        error: null,
        pagination: response.pagination,
      });

      if (params) {
        setQueryParams(params);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
    }
  }, [queryParams]);

  const createContact = useCallback(
    async (data: CreateContactData): Promise<Contact | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const newContact = await api.contacts.create(data);
        setState((prev) => ({ ...prev, loading: false }));
        return newContact;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error as ApiError,
        }));
        return null;
      }
    },
    []
  );

  const deleteContact = useCallback(async (id: string): Promise<boolean> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await api.contacts.delete(id);
      setState((prev) => ({
        ...prev,
        contacts: prev.contacts.filter((c) => c.id !== id),
        loading: false,
      }));
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as ApiError,
      }));
      return false;
    }
  }, []);

  const refresh = useCallback(() => {
    return fetchContacts(queryParams);
  }, [fetchContacts, queryParams]);

  useEffect(() => {
    if (autoFetch) {
      fetchContacts();
    }
  }, [autoFetch, fetchContacts]);

  return {
    ...state,
    fetchContacts,
    createContact,
    deleteContact,
    refresh,
  };
};
